import React, {useState, useEffect} from 'react';

import {isMobileDevice, MOBILE_VEW, DESKTOP_VIEW} from '../../utils';
import {getFromStorage, setToStorage} from '../../storage';

import {forceDesktopView, forceMobileView} from './view';
import styles from './styles.css';
import {IconMobile} from './IconMobile';
import {IconDesktop} from './IconDesktop';

const MOBILE_VIEW_KEY = 'mobile-view';

const userPrefersDesktopView = () => getFromStorage(MOBILE_VIEW_KEY) === 'DESKTOP';
const userPrefersMobileView = () => getFromStorage(MOBILE_VIEW_KEY) === 'MOBILE';

const getScaleFactor = () => 770 / ((window.screen && window.screen.width) || 770);
const isLandscape = () => {
  try {
    const screen = (window as Window).screen;

    if ('orientation' in window) {
      return (window.orientation === -90 || window.orientation === 90) && screen.width >= 650;
    } else if ('screen' in window) {
      const orientation: string | void =
        // @ts-ignore
        screen.msOrientation || screen.mozOrientation || (screen.orientation || {}).type;

      return /landscape/.test(orientation || '') && screen.width >= 650;
    }
  } catch (error) {
    // Ignore
  }

  return false;
};

const pageLoadsInLandscape = isLandscape();

if (!userPrefersMobileView() && (userPrefersDesktopView() || pageLoadsInLandscape)) {
  forceDesktopView();
}
export const ViewToggleBox = () => {
  const desktopViewMql = window.matchMedia('(min-width: 770px)');
  const [desktopView, setDesktopView] = useState(desktopViewMql.matches);
  const [portraitMode, setPortraitMode] = useState(!isLandscape());
  const [scaleFactor, setScaleFactor] = useState(getScaleFactor());

  useEffect(() => {
    const handleOrientationChange = () => {
      setPortraitMode(!isLandscape());
      setScaleFactor(getScaleFactor());

      if (isLandscape() && !userPrefersMobileView()) {
        forceDesktopView();
      } else if (!userPrefersDesktopView()) {
        forceMobileView();
      }
    };

    const handleViewChange = (mql: MediaQueryList) => {
      if (portraitMode) {
        setDesktopView(mql.matches);
        setScaleFactor(getScaleFactor());
      }

      if (mql.matches) {
        document.body.classList.remove(MOBILE_VEW);
        document.body.classList.add(DESKTOP_VIEW);
      } else {
        document.body.classList.add(MOBILE_VEW);
        document.body.classList.remove(DESKTOP_VIEW);
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    // @ts-ignore
    desktopViewMql.addListener(handleViewChange);

    // NOTE: there is a tricky race condition since the moment the component
    // executes and the moment the useEffect runs that prevents desktopView state
    // to be in sync with desktopViewMql.matches
    // It gets fixed with the code below.
    requestAnimationFrame(() => {
      handleViewChange(desktopViewMql);
      handleOrientationChange();
    });

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      // @ts-ignore
      desktopViewMql.removeListener(handleViewChange);
    };
  });

  const buttonClickHandler = () => {
    if (desktopViewMql.matches) {
      forceMobileView();
      setDesktopView(false);
      setToStorage(MOBILE_VIEW_KEY, 'MOBILE');
    } else {
      forceDesktopView();
      setDesktopView(true);
      setToStorage(MOBILE_VIEW_KEY, 'DESKTOP');
    }
  };

  if (!isMobileDevice() || !portraitMode) {
    return null;
  }

  return (
    <button aria-label="toggle view" className={styles.viewToggleBox} onClick={buttonClickHandler}>
      {!desktopView && (
        <div
          className={styles.boxContentWrapper}
          style={{
            fontSize: `14px`,
            height: `30px`
          }}
        >
          <IconDesktop
            className={styles.iconDesktop}
            style={{
              height: '20px',
              padding: '0 10px',
              width: '16px'
            }}
          />
          <span>Desktop view</span>
        </div>
      )}
      {desktopView && (
        <div
          className={styles.boxContentWrapper}
          style={{
            fontSize: `${14 * scaleFactor}px`,
            height: `${30 * scaleFactor}px`
          }}
        >
          <IconMobile
            className={styles.iconMobile}
            style={{
              height: `${16 * scaleFactor}px`,
              padding: `0 ${10 * scaleFactor}px`,
              width: `${10 * scaleFactor}px`
            }}
          />
          <span>Mobile view</span>
        </div>
      )}
    </button>
  );
};

Object.defineProperty(ViewToggleBox, 'name', {value: 'ViewToggleBox'});
